import { Draft } from '@weblite/ts-action/draft'
import { useCallback, useEffect, useRef } from 'react'
import {
  asyncDelay,
  useLastCallback,
  useStateRef,
} from '@weblite/logic-toolkit'
import { useHistoryCustomBack, useNavigate } from 'weblite-router'
import { WithBodyMessage } from '@weblite/ts-action/message'
import {
  cancelSetDraft,
  deleteDraft,
  getDraft,
  setDraft,
  setImmediateDraft,
} from '$logic/draft'
import { getMessageByIid } from '$logic/message'
import { SendFieldAction } from './send-field.type'
import { editorAPI } from './components/text-editor'
import { getOpenedRoomId } from '$logic/opened-room'
import { isPhoneOrTablet } from '$func/environmentInfo'
import { dateWithOffset } from '$logic/client-device/time-offset.service'

interface Props {
  roomId: string
  action: SendFieldAction
  setAction: (action: SendFieldAction) => unknown
  onTypingEvent: () => unknown
}

export function useDraft({ roomId, setAction, action, onTypingEvent }: Props) {
  const prevTextRef = useRef<string>('')
  const prevTextDateRef = useRef<number>(0)
  const actionRef = useStateRef(action)

  const isDraftChanged = useLastCallback(
    (openedRoomId: string, newText: string, newAction: SendFieldAction) => {
      const prevDraft = getDraft(openedRoomId) as Draft | undefined

      if (!prevDraft) {
        if (!newText.trim() && newAction.type === 'NEW_MESSAGE') return false
        return true
      }

      if (
        prevDraft.value.body !== newText ||
        prevDraft.value.type !== newAction.type
      )
        return true

      if (newAction.type === 'REPLY')
        return newAction.iid !== prevDraft.value.iid

      if (newAction.type === 'EDIT')
        return newAction.message.iid !== prevDraft.value.iid

      if (newAction.type === 'FORWARD')
        return (
          newAction.forward.iids.join(',') !==
          prevDraft.value.forward?.iids.join(',')
        )

      return false
    },
  )

  useEffect(() => {
    if (!roomId) return

    const draft = getDraft(roomId)
    const { body, type, iid, forward } = draft?.value || {}

    if (type === 'EDIT') {
      if (!iid) return
      getMessageByIid(roomId, iid).then(message => {
        if (message && !message.patches?.deleted) {
          setAction({ type, message: message as WithBodyMessage })
        }
      })
    } else if (type === 'FORWARD') {
      if (forward) setAction({ type, forward })
    } else if (type === 'REPLY') {
      if (iid) setAction({ type, iid })
    } else setAction({ type: 'NEW_MESSAGE' })

    // Note: We need to delay setText to ensure action state is updated in callbacks.
    // editorAPI.setText is not a React setState - it's an external function that immediately
    // triggers onEditorTextChange. Since we just called setAction, the callback would see
    // the previous action value. The asyncDelay ensures the action state is updated first.
    asyncDelay(0).then(() => {
      editorAPI.setText(body || '')
    })

    if (isPhoneOrTablet) editorAPI.blur()

    // eslint-disable-next-line consistent-return
    return () => {
      cancelSetDraft()

      const text = prevTextRef.current
      const date = prevTextDateRef.current
      prevTextRef.current = ''
      prevTextDateRef.current = 0

      // eslint-disable-next-line react-hooks/exhaustive-deps
      const currentAction = actionRef.current

      if (!isDraftChanged(roomId, text, currentAction)) return

      const draftMeta =
        currentAction.type === 'EDIT'
          ? { type: currentAction.type, iid: currentAction.message.iid }
          : currentAction

      if (text.trim() || currentAction.type !== 'NEW_MESSAGE')
        setImmediateDraft(roomId, { body: text, ...draftMeta }, date)
      else deleteDraft(roomId)
    }
  }, [roomId, actionRef, setAction, isDraftChanged])

  const onDraftChange = useCallback(
    (text: string) => {
      prevTextRef.current = text
      prevTextDateRef.current = dateWithOffset()
      const currentAction = actionRef.current

      if (isDraftChanged(roomId, text, currentAction)) {
        const draftMeta =
          currentAction.type === 'EDIT'
            ? { type: currentAction.type, iid: currentAction.message.iid }
            : currentAction
        if (text.trim() || currentAction.type !== 'NEW_MESSAGE') {
          setDraft(
            roomId,
            { body: text, ...draftMeta },
            prevTextDateRef.current,
          )

          onTypingEvent()
        } else {
          cancelSetDraft()
          deleteDraft(getOpenedRoomId())
        }
      } else {
        cancelSetDraft()
      }
    },
    [roomId, onTypingEvent, isDraftChanged, actionRef],
  )

  return { onDraftChange }
}

export function useFullscreenModeOnMediaOpen(
  messageType: WithBodyMessage['type'],
  makeSendFieldFullscreen: () => void,
  onCancel: () => void,
) {
  const navigate = useNavigate()

  useHistoryCustomBack(
    () =>
      messageType === 'IMAGE' || messageType === 'VIDEO'
        ? onCancel()
        : navigate(-1),
    [messageType],
  )

  useEffect(() => {
    const pushPreviewStateToRoomPanel =
      messageType === 'IMAGE' || messageType === 'VIDEO'

    if (pushPreviewStateToRoomPanel) {
      makeSendFieldFullscreen()
      navigate('', {
        data: {
          roomId: getOpenedRoomId(),
          isInEditMedia: true,
        },
      })
    }

    return () => {
      if (pushPreviewStateToRoomPanel) navigate(-1)
    }
  }, [])
}
